import { useEffect, useState } from 'react';
import { LIBRARY_DOCUMENTS, type LibraryDocumentId } from './libraryApi';

/**
 * When this browser last opened each library document. Library documents are
 * not `folders_nodes`, so the server's open history cannot hold them; like the
 * rest of a reader's library view, this lives in localStorage.
 */
const STORAGE_KEY = 'numtable.library.opens';
const CHANGED = 'numtable:library-opens';

export interface LibraryOpen {
  readonly id: LibraryDocumentId;
  /** ISO timestamp. */
  readonly openedAt: string;
}

function read(): LibraryOpen[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object') return [];
    return Object.entries(parsed as Record<string, unknown>)
      .filter((entry): entry is [LibraryDocumentId, string] =>
        (LIBRARY_DOCUMENTS as readonly string[]).includes(entry[0]) && typeof entry[1] === 'string')
      .map(([id, openedAt]) => ({ id, openedAt }))
      .sort((a, b) => b.openedAt.localeCompare(a.openedAt));
  } catch {
    return [];
  }
}

export function recordLibraryOpen(id: LibraryDocumentId) {
  try {
    const current = Object.fromEntries(read().map((open) => [open.id, open.openedAt]));
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, [id]: new Date().toISOString() }));
    window.dispatchEvent(new Event(CHANGED));
  } catch {
    // Storage unavailable: the document just won't show in Last opened.
  }
}

/** Newest first; follows opens in this tab and in others. */
export function useLibraryRecents(): readonly LibraryOpen[] {
  const [opens, setOpens] = useState(read);
  useEffect(() => {
    const refresh = () => setOpens(read());
    window.addEventListener(CHANGED, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(CHANGED, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  return opens;
}
